/**
 * Integrytas Group - Common Security Module (CSM)
 * Presentation Context — density, font size, and the active THEME.
 *
 * Single owner of look-and-feel application (CLAUDE §0 "extend, don't duplicate"):
 * it applies the selected theme's palette/font/mode (via documentElement inline
 * CSS vars) and the density/font-size data-attributes. Built-in presets ship in
 * code; ORG/USER themes load from csm_theme. The selection + density/font-size
 * persist to csm_ui_preference (GLOBAL/presentation) so they survive reloads and
 * carry across devices; the last theme is also cached in localStorage so the
 * pre-auth login screen paints themed instantly.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getDdmSchema } from './ddm_mode';
import type { AuthSession } from '../types/auth';
import { applyTheme, applyDensityFontSize } from './theme/theme_tokens';
import type { Density, FontSize, ThemeDefinition } from './theme/theme_tokens';
import { BUILTIN_THEMES, DEFAULT_THEME, DEFAULT_PALETTE } from './theme/theme_presets';
import {
  loadDbThemes, loadAppDefaultCode, loadPresentationPref, savePresentationPref,
  saveTheme, deleteThemeRow, setAppDefaultCode, cacheTheme, type PresentationPref,
} from './theme/theme_store';

export type { Density, FontSize } from './theme/theme_tokens';

export interface PresentationState {
  density:   Density;
  fontSize:  FontSize;
  setDensity:  (v: Density)  => void;
  setFontSize: (v: FontSize) => void;

  // Theming
  themeCode:   string;
  activeTheme: ThemeDefinition;
  themes:      ThemeDefinition[];
  isAdmin:     boolean;
  setThemeCode:    (code: string) => void;
  reloadThemes:    () => Promise<void>;
  saveUserTheme:   (def: ThemeDefinition) => Promise<void>;
  publishOrgTheme: (def: ThemeDefinition) => Promise<void>;
  deleteUserTheme: (def: ThemeDefinition) => Promise<void>;
  setAppDefault:   (code: string) => Promise<void>;
  /** Live-preview a definition without persisting; pass null to end preview. */
  preview: (def: ThemeDefinition | null) => void;
}

const DEFAULTS = { density: 'compact' as Density, fontSize: 'medium' as FontSize };

/**
 * The theme demo mode reaches for. Seeded as an editable ORG row by
 * 20260839000000_ddm_demo_theme.sql — red by default, but a tenant may recolour
 * or rename it, so this is only the code we look for.
 */
const DEMO_THEME_CODE = 'DEMO';

function mergeThemes(builtins: ThemeDefinition[], db: ThemeDefinition[]): ThemeDefinition[] {
  const codes = new Set(builtins.map((t) => t.code));
  return [...builtins, ...db.filter((t) => !codes.has(t.code))];
}

const NOOP_ASYNC = async () => { /* replaced by provider */ };

const NOOP = () => { /* replaced by the provider */ };

export const PresentationContext = createContext<PresentationState>({
  ...DEFAULTS,
  setDensity:  NOOP,
  setFontSize: NOOP,
  themeCode:   DEFAULT_THEME.code,
  activeTheme: DEFAULT_THEME,
  themes:      BUILTIN_THEMES,
  isAdmin:     false,
  setThemeCode:    NOOP,
  reloadThemes:    NOOP_ASYNC,
  saveUserTheme:   NOOP_ASYNC,
  publishOrgTheme: NOOP_ASYNC,
  deleteUserTheme: NOOP_ASYNC,
  setAppDefault:   NOOP_ASYNC,
  preview:         NOOP,
});

export function usePresentationState(session: AuthSession): PresentationState {
  const [density,  setDensityRaw]  = useState<Density>(DEFAULTS.density);
  const [fontSize, setFontSizeRaw] = useState<FontSize>(DEFAULTS.fontSize);
  const [themes,   setThemes]      = useState<ThemeDefinition[]>(BUILTIN_THEMES);
  const [themeCode, setThemeCodeState] = useState<string>(DEFAULT_THEME.code);
  const [previewDef, setPreviewDef]    = useState<ThemeDefinition | null>(null);
  const prefRef = useRef<PresentationPref>({});

  const isAdmin = useMemo(() => {
    const pa = session.effective_user.page_access;
    return Array.isArray(pa) && pa.length > 0 && pa.every((p) => p.can_view && p.can_change);
  }, [session]);

  const activeTheme = useMemo(
    () => themes.find((t) => t.code === themeCode) ?? DEFAULT_THEME,
    [themes, themeCode],
  );

  // Load themes + app default + persisted selection once per user.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [db, appDefault, pref] = await Promise.all([
        loadDbThemes(),
        loadAppDefaultCode(),
        loadPresentationPref(session.effective_user.user_id),
      ]);
      if (cancelled) return;
      const all = mergeThemes(BUILTIN_THEMES, db);
      setThemes(all);
      prefRef.current = pref;
      if (pref.density  && ['compact', 'comfortable'].includes(pref.density))   setDensityRaw(pref.density);
      if (pref.fontSize && ['small', 'medium', 'large'].includes(pref.fontSize)) setFontSizeRaw(pref.fontSize);
      // In demo mode the demo theme outranks the saved preference, so the
      // sandbox never wears production's colours. Read once here rather than
      // reactively: switching schema reloads the app (switchDdmSchema), so this
      // effect re-runs with the new value anyway.
      //
      // A DEFAULT, not a lock — setThemeCode() still works, so someone can
      // change theme while in demo; the next load returns to the demo theme.
      // And if the row has been deleted, this falls through to the normal
      // chain rather than failing.
      const demoTheme = getDdmSchema() === 'demo' && all.some((t) => t.code === DEMO_THEME_CODE)
        ? DEMO_THEME_CODE
        : null;
      const resolved =
        demoTheme ? demoTheme
        : pref.theme_code && all.some((t) => t.code === pref.theme_code) ? pref.theme_code
        : appDefault && all.some((t) => t.code === appDefault) ? appDefault
        : DEFAULT_THEME.code;
      setThemeCodeState(resolved);
    })();
    return () => { cancelled = true; };
  }, [session.effective_user.user_id]);

  // Apply palette/font/mode (preview overrides the persisted active theme).
  useEffect(() => {
    const def = previewDef ?? activeTheme;
    applyTheme(def, DEFAULT_PALETTE);
    if (!previewDef) cacheTheme(def);
  }, [activeTheme, previewDef]);

  // Apply density / font-size (single writer of these data-attributes).
  useEffect(() => { applyDensityFontSize(density, fontSize); }, [density, fontSize]);

  const persist = useCallback((patch: PresentationPref) => {
    const current = prefRef.current;
    prefRef.current = { ...current, ...patch };
    void savePresentationPref(patch, current, session);
  }, [session]);

  const setDensity  = useCallback((v: Density)  => { setDensityRaw(v);  persist({ density:  v }); }, [persist]);
  const setFontSize = useCallback((v: FontSize) => { setFontSizeRaw(v); persist({ fontSize: v }); }, [persist]);

  const setThemeCode = useCallback((code: string) => {
    setThemeCodeState(code);
    setThemes((cur) => {
      const t = cur.find((x) => x.code === code) ?? DEFAULT_THEME;
      setDensityRaw(t.density);
      setFontSizeRaw(t.fontSize);
      persist({ theme_code: code, density: t.density, fontSize: t.fontSize });
      return cur;
    });
  }, [persist]);

  const reloadThemes = useCallback(async () => {
    const db = await loadDbThemes();
    setThemes(mergeThemes(BUILTIN_THEMES, db));
  }, []);

  const saveUserTheme = useCallback(async (def: ThemeDefinition) => {
    await saveTheme(def, session, 'USER');
    await reloadThemes();
    setThemeCode(def.code);
  }, [session, reloadThemes, setThemeCode]);

  const publishOrgTheme = useCallback(async (def: ThemeDefinition) => {
    await saveTheme(def, session, 'ORG');
    await reloadThemes();
  }, [session, reloadThemes]);

  const deleteUserTheme = useCallback(async (def: ThemeDefinition) => {
    if (def.theme_id != null) await deleteThemeRow(def.theme_id);
    await reloadThemes();
    if (themeCode === def.code) setThemeCode(DEFAULT_THEME.code);
  }, [reloadThemes, themeCode, setThemeCode]);

  const setAppDefault = useCallback(async (code: string) => {
    await setAppDefaultCode(code, session);
  }, [session]);

  const preview = useCallback((def: ThemeDefinition | null) => { setPreviewDef(def); }, []);

  return {
    density, fontSize, setDensity, setFontSize,
    themeCode, activeTheme, themes, isAdmin,
    setThemeCode, reloadThemes, saveUserTheme, publishOrgTheme, deleteUserTheme, setAppDefault, preview,
  };
}

export function usePresentation(): PresentationState {
  return useContext(PresentationContext);
}
